/**
 * `<xa-donut>` — donut chart (default variant A "standard ring").
 * Registered as `xaDonut`. Arcs are stroked paths starting at 12 o'clock,
 * clockwise; hovering grows the stroke by 6px (140ms, from the shared CSS).
 * Toggling legend slices recomputes the centre total unless `center-value`
 * is pinned.
 *
 * Bindings:
 * | Binding | Type | Default | Notes |
 * |---|---|---|---|
 * | `data` | `XaSlice[]` | — | required |
 * | `colors` | `XaPalette` | `'brand'` | global `xaChartsConfig.palette` wins over the default |
 * | `size` | `number` | `240` | square SVG px |
 * | `thickness` | `number` | `0.30` | ring thickness as fraction of radius (B thin = 0.14) |
 * | `variant` | `'solid' \| 'segmented'` | `'solid'` | segmented = 0.055rad gaps + round caps |
 * | `center-label` | `string` | `'Total'` | |
 * | `center-value` | `string` | auto | pinned centre value; default `fmt(sum of visible slices)` |
 * | `legend` | `'right' \| 'bottom' \| 'none'` | `'right'` | right = column layout in a flex row |
 * | `legend-values` | `boolean` | `true` | |
 * | `on-series-toggle` | `&` callback | — | `$event: XaSeriesToggleEvent` |
 * | `on-slice-hover` | `&` callback | — | `$event: XaSliceHoverEvent` |
 * | `on-slice-click` | `&` callback | — | `$event: XaSliceClickEvent` |
 */
import {
  computeDonut,
  donutTooltip,
  ensureChartStyles,
  legendLayoutFor,
  normalizeSlices,
  sliceAriaLabel,
} from '@xait/charts-core';
import type {
  XaDonutArc,
  XaDonutLayout,
  XaLegendLayout,
  XaPalette,
  XaSeriesToggleEvent,
  XaSlice,
  XaSliceClickEvent,
  XaSliceHoverEvent,
} from '@xait/charts-core';
import { XaChartBaseController, XAC_SLICE_TABLE, XAC_TIP_TEMPLATE } from './chart-base';
import type { XaCallback } from './chart-base';
import type { XaChartsDefaults } from './config';

class XaDonutController extends XaChartBaseController {
  static override $inject = ['$element', 'xaChartsConfig'];

  /* bindings */
  data?: XaSlice[];
  colors?: XaPalette;
  size?: number;
  thickness?: number;
  variant?: 'solid' | 'segmented';
  centerLabel?: string;
  centerValue?: string;
  legend?: 'right' | 'bottom' | 'none';
  legendValues?: boolean;
  onSeriesToggle!: XaCallback<XaSeriesToggleEvent>;
  onSliceHover!: XaCallback<XaSliceHoverEvent>;
  onSliceClick!: XaCallback<XaSliceClickEvent>;

  /* view state */
  layout: XaDonutLayout | null = null;
  legendPos: 'right' | 'bottom' | 'none' = 'right';
  legendLayout: XaLegendLayout = 'column';
  legendValuesOn = true;
  hoverLabel: string | null = null;
  ariaLabel = '';

  private readonly hidden = new Set<string>();

  constructor($element: ng.IAugmentedJQuery, private readonly xaChartsConfig: XaChartsDefaults) {
    super($element);
  }

  $onInit(): void {
    ensureChartStyles();
    this.recompute();
  }

  $onChanges(): void {
    this.recompute();
  }

  toggleSlice(label: string): void {
    const nowHidden = !this.hidden.delete(label);
    if (nowHidden) this.hidden.add(label);
    this.recompute();
    this.onSeriesToggle({ $event: { name: label, hidden: nowHidden, hiddenAll: Array.from(this.hidden) } });
  }

  isolateSlice(label: string): void {
    const all = (this.layout?.legend ?? []).map((it) => it.label);
    const isolated =
      all.length > 1 && !this.hidden.has(label) && all.every((n) => n === label || this.hidden.has(n));
    this.hidden.clear();
    if (!isolated) {
      for (const n of all) if (n !== label) this.hidden.add(n);
    }
    this.recompute();
    this.onSeriesToggle({
      $event: { name: label, hidden: this.hidden.has(label), hiddenAll: Array.from(this.hidden) },
    });
  }

  hoverArc(ev: MouseEvent, arc: XaDonutArc): void {
    this.showTip(ev, donutTooltip(arc));
    if (this.hoverLabel !== arc.label) {
      this.hoverLabel = arc.label;
      this.onSliceHover({ $event: { label: arc.label } });
    }
  }

  leaveArc(): void {
    this.hideTip();
    if (this.hoverLabel !== null) {
      this.hoverLabel = null;
      this.onSliceHover({ $event: { label: null } });
    }
  }

  clickArc(arc: XaDonutArc): void {
    this.onSliceClick({ $event: { label: arc.label, value: arc.value } });
  }

  private recompute(): void {
    if (this.data) {
      this.data = normalizeSlices(this.data);
    }
    this.legendPos = this.legend ?? 'right';
    this.legendValuesOn = this.legendValues ?? true;
    this.legendLayout = legendLayoutFor('donut', this.legendPos, this.legendValuesOn);
    if (!this.data) {
      this.layout = null;
      return;
    }
    this.layout = computeDonut(this.data, {
      size: this.size ?? 240,
      thickness: this.thickness ?? 0.3,
      variant: this.variant ?? 'solid',
      colors: this.colors ?? this.xaChartsConfig.palette ?? 'brand',
      hidden: this.hidden,
      centerLabel: this.centerLabel ?? 'Total',
      centerValue: this.centerValue,
    });
    this.ariaLabel = sliceAriaLabel('Donut', this.data);
  }
}

export const xaDonutComponent: ng.IComponentOptions = {
  controller: XaDonutController,
  bindings: {
    data: '<',
    colors: '<',
    size: '<',
    thickness: '<',
    variant: '<',
    centerLabel: '<',
    centerValue: '<',
    legend: '<',
    legendValues: '<',
    onSeriesToggle: '&',
    onSliceHover: '&',
    onSliceClick: '&',
  },
  template: `<div class="xac-chart">
  <div ng-if="$ctrl.layout" class="xac-flex" ng-class="{ 'xac-flex-column': $ctrl.legendPos === 'bottom' }">
    <svg class="xac-svg" role="img" aria-label="{{$ctrl.ariaLabel}}" ng-attr-width="{{$ctrl.layout.size}}" ng-attr-height="{{$ctrl.layout.size}}">
      <path ng-repeat="arc in $ctrl.layout.arcs track by $index" class="xac-donut-arc" fill="none"
          ng-attr-d="{{arc.path}}"
          ng-attr-stroke="{{arc.color}}"
          ng-attr-stroke-width="{{$ctrl.hoverLabel === arc.label ? $ctrl.layout.hoverStrokeWidth : $ctrl.layout.strokeWidth}}"
          ng-attr-stroke-linecap="{{$ctrl.layout.linecap}}"
          ng-mousemove="$ctrl.hoverArc($event, arc)"
          ng-mouseleave="$ctrl.leaveArc()"
          ng-click="$ctrl.clickArc(arc)"></path>
      <text class="xac-center-value" text-anchor="middle" ng-attr-x="{{$ctrl.layout.cx}}" ng-attr-y="{{$ctrl.layout.centerValueY}}">{{$ctrl.layout.centerValue}}</text>
      <text class="xac-center-label" text-anchor="middle" ng-attr-x="{{$ctrl.layout.cx}}" ng-attr-y="{{$ctrl.layout.centerLabelY}}">{{$ctrl.layout.centerLabel}}</text>
    </svg>
    <xac-legend ng-if="$ctrl.legendPos !== 'none'" items="$ctrl.layout.legend" layout="$ctrl.legendLayout" show-values="$ctrl.legendValuesOn" on-toggle="$ctrl.toggleSlice(label)" on-isolate="$ctrl.isolateSlice(label)"></xac-legend>
  </div>
  ${XAC_SLICE_TABLE}
  ${XAC_TIP_TEMPLATE}
</div>`,
};
